<template>
  <div
    class="shopping-cart-popup"
    :class="{ 'shopping-cart-popup--empty': displayItems.length === 0 }"
  >
    <div ref="listRef" class="shopping-cart-popup__list" @scroll="handleListScroll">
      <template v-if="displayItems.length > 0">
        <div v-for="item in displayItems" :key="item.id" class="shopping-cart-popup__item">
          <div class="shopping-cart-popup__item-img-wrap">
            <BaseImage
              :src="item.image"
              :alt="item.displayName"
              class="shopping-cart-popup__item-img"
            />
          </div>
          <div class="shopping-cart-popup__item-info">
            <NuxtLink
              v-if="canOpenProductDetail(item)"
              class="shopping-cart-popup__item-name shopping-cart-popup__item-name--link"
              :title="item.displayName"
              :to="`/store/products/${item.prodId}`"
              @click="handleViewDetail"
            >
              {{ item.displayName }}
            </NuxtLink>
            <div v-else class="shopping-cart-popup__item-name" :title="item.displayName">
              {{ item.displayName }}
            </div>
            <span v-if="getCartItemStatusLabel(item)" role="status">{{
              getCartItemStatusLabel(item)
            }}</span>
            <div class="shopping-cart-popup__item-meta">
              <span class="shopping-cart-popup__item-price">¥{{ formatPrice(item.price) }}</span>
              <span class="shopping-cart-popup__item-quantity">x {{ item.quantity }}</span>
            </div>
          </div>
        </div>
      </template>
      <div v-else class="shopping-cart-popup__empty">
        <SvgIcon
          aria-hidden="true"
          name="add-shopping-cart"
          size="32"
          class="shopping-cart-popup__empty-icon"
        />
        <p class="shopping-cart-popup__empty-text">您的购物车是空的</p>
      </div>
    </div>
    <div
      v-if="displayItems.length > 0"
      class="shopping-cart-popup__footer"
      :class="{ 'has-shadow': isScrolled }"
    >
      <div class="shopping-cart-popup__summary">
        <span class="shopping-cart-popup__summary-count">{{ summaryCount }}台商品</span>
        <span class="shopping-cart-popup__summary-price">¥{{ formatPrice(finalMoney) }}</span>
      </div>
      <button class="shopping-cart-popup__checkout" @click="handleCheckout">去结算</button>
    </div>
  </div>
</template>

<script setup>
/**
 * @description 购物车浮窗，展示已选商品列表、台数、总价（接口 data.totalFinalAmount）；空状态去挑选商品跳转商城首页，有数据去结算跳转购物车页
 * @emits checkout - 点击去结算时触发，跳转购物车页
 * @emits go-shopping - 点击去挑选商品时触发，跳转商城首页
 * @emits view-detail - 点击商品名称跳转详情时触发，用于关闭头部浮窗
 */

import { computed, ref, onMounted, watch, nextTick } from 'vue'
import {
  canViewCartItemDetail,
  getCartItemStatusLabel,
  useStoreCartStore,
} from '~/store/store/cart'
import { formatPrice } from '@/utils'
import { formatStoreProductDisplayName } from '~/utils/store/formatStoreProductDisplayName'

const emit = defineEmits(['checkout', 'go-shopping', 'view-detail'])

const cartStore = useStoreCartStore()
const listRef = ref(null)
const isScrolled = ref(false)

const handleListScroll = (e) => {
  const el = e.target
  const canScroll = el.scrollHeight > el.clientHeight
  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 1
  isScrolled.value = canScroll && !atBottom
}

const displayItems = computed(() =>
  (cartStore.headerDisplayItems || []).map((item) => ({
    ...item,
    displayName: formatStoreProductDisplayName(item),
  }))
)

const summaryCount = computed(() => cartStore.headerTotalCount)

const finalMoney = computed(() => cartStore.headerFinalMoney)

const canOpenProductDetail = (item) => canViewCartItemDetail(item)

watch(
  displayItems,
  () => {
    nextTick(() => {
      if (listRef.value) {
        const el = listRef.value
        const canScroll = el.scrollHeight > el.clientHeight
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 1
        isScrolled.value = canScroll && !atBottom
      }
    })
  },
  { deep: true }
)

onMounted(() => {
  nextTick(() => {
    if (listRef.value) {
      const el = listRef.value
      const canScroll = el.scrollHeight > el.clientHeight
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 1
      isScrolled.value = canScroll && !atBottom
    }
  })
})

const handleCheckout = () => {
  emit('checkout')
}

const handleGoShopping = () => {
  emit('go-shopping')
}

const handleViewDetail = () => {
  emit('view-detail')
}
</script>

<style lang="scss" scoped>
.shopping-cart-popup {
  position: relative;
  width: 268px;
  max-height: 464px;
  padding: 16px 24px 20px;
  background-color: $bg-white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba($color-black, 0.12);
  border: 1px solid $border-dashed-color;
  display: flex;
  flex-direction: column;

  &--empty {
    height: 96px;
    max-height: 96px;
    padding: 0;

    .shopping-cart-popup__list {
      display: flex;
      align-items: center;
      justify-content: center;
      padding-bottom: 0;
    }
  }

  &__list {
    flex: 1;
    min-height: 0;
    max-height: 280px;
    overflow-y: auto;
    padding-bottom: $spacing-md;
    scrollbar-width: none;
    -ms-overflow-style: none;
    display: flex;
    flex-direction: column;
    gap: 12px;

    &::-webkit-scrollbar {
      width: 0;
      height: 0;
    }
  }

  &__footer {
    flex-shrink: 0;
    position: relative;
    background-color: $bg-white;
    margin: 0 -24px -20px;
    padding: 12px 24px 20px;
    z-index: 1;
    border-top: 1px solid $border-dashed-color;
    border-radius: 0 0 8px 8px;

    &.has-shadow {
      box-shadow: 0 -8px 16px rgba($color-black, 0.08);
    }
  }

  &__item {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  &__item-img-wrap {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 4px;
    overflow: hidden;
    background-color: $bg-color;
  }

  &__item-img {
    width: 100%;
    height: 100%;

    :deep(img) {
      object-fit: contain;
    }
  }

  &__item-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
  }

  &__item-name {
    display: block;
    font-family:
      'Alibaba PuHuiTi',
      -apple-system,
      BlinkMacSystemFont,
      sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 22px;
    color: $text-title-color;
    text-decoration: none;
    @include text-ellipsis(2);

    &--link {
      cursor: pointer;
      transition: color 0.2s ease;

      &:hover,
      &:focus-visible {
        color: $primary-color;
      }
    }
  }

  &__item-meta {
    font-size: 14px;
    color: $text-subtitle-color;
    display: flex;
    align-items: center;
    line-height: 22px;
    gap: 12px;
  }

  &__item-price {
    color: $text-title-color;
    font-weight: 400;
  }

  &__item-quantity {
    color: $text-subtitle-color;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 100%;
    text-align: center;
  }

  &__empty-icon {
    color: $border-hover-color;
  }

  &__empty-text {
    margin: 0;
    font-family:
      'Alibaba PuHuiTi',
      -apple-system,
      BlinkMacSystemFont,
      sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    color: $text-subtitle-color;
  }

  &__summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    font-size: $font-size-card-content;
    color: $text-subtitle-color;
    line-height: 18px;
  }

  &__summary-price {
    font-size: $font-size-card-content;
    font-weight: 400;
    color: $text-subtitle-color;
  }

  &__checkout {
    width: 100%;
    height: 40px;
    font-size: $font-size-card-subtitle;
    border-radius: $radius-sm;
    font-weight: 400;
    background-color: $primary-color;
    border: none;
    color: $bg-white;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      filter: brightness(0.95);
    }

    &:disabled {
      background-color: $border-dashed-color;
      cursor: not-allowed;
    }
  }
}
</style>
